/**
 * ConversionTimingAnalysisPage — read-only report for FS-303.
 *
 * Shows:
 * - Overall latency histogram (click -> conversion) as a vertical bar chart.
 * Brand green bars (green-500) with tabular counts above.
 * - Percentile summary tiles (median, p90, p99).
 * - Per-dimension breakdown (campaign | affiliate) as a table with a mini
 * inline bar for each bucket's share.
 *
 * Data from GET /api/v1/conversion-timing-analysis/{histogram,breakdown}.
 */
import { useState } from "react";
import {
	useConversionTimingAnalysisBreakdown,
	useConversionTimingAnalysisHistogram,
} from "./hooks";
import type {
	BreakdownRow,
	Dimension,
	HistogramBucket,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH =
	"px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
// Brand green only. green-500 is the brand chart fill.
const BAR_FILL = "bg-green-500";

function formatLatency(sec: number | null): string {
	if (sec === null || !Number.isFinite(sec)) return "—";
	if (sec < 60) return `${sec}s`;
	if (sec < 3600) return `${Math.round(sec / 60)}m`;
	if (sec < 86400) return `${Math.round(sec / 3600)}h`;
	return `${Math.round(sec / 86400)}d`;
}

function maxCount(buckets: readonly HistogramBucket[]): number {
	let m = 0;
	for (const b of buckets) if (b.count > m) m = b.count;
	return m;
}

function barHeightPct(count: number, max: number): number {
	if (max <= 0) return 0;
	const pct = (count / max) * 100;
	return pct < 2 && count > 0 ? 2 : pct;
}

function sharePct(count: number, total: number): number {
	if (total <= 0) return 0;
	return (count / total) * 100;
}

export function ConversionTimingAnalysisPage() {
	const [dimension, setDimension] = useState<Dimension>("campaign");
	const histQuery = useConversionTimingAnalysisHistogram();
	const breakdownQuery = useConversionTimingAnalysisBreakdown({ by: dimension });

	const hist = histQuery.data;
	const buckets: HistogramBucket[] = hist?.buckets ?? [];
	const total = hist?.total ?? 0;
	const hMax = maxCount(buckets);

	const breakdown: BreakdownRow[] = breakdownQuery.data?.breakdown ?? [];

	return (
 <main
 data-testid="conversion-timing-analysis-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 Report
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Conversion Timing Analysis
 </h1>
 <p className="text-sm text-zinc-600">
 Click-to-conversion latency distribution across pairs.
 </p>
 </header>

 {/* Percentile tiles */}
 <section
 className="grid grid-cols-1 gap-4 sm:grid-cols-4"
 aria-label="Percentile summary"
 >
 <div className={CARD}>
 <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
 Total pairs
 </p>
 <p
 className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900"
 data-testid="conversion-timing-analysis-total"
 >
 {histQuery.isLoading ? "…" : total.toLocaleString()}
 </p>
 </div>
 <div className={CARD}>
 <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
 Median
 </p>
 <p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900">
 {histQuery.isLoading ? "…" : formatLatency(hist?.median_sec ?? null)}
 </p>
 </div>
 <div className={CARD}>
 <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
 p90
 </p>
 <p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900">
 {histQuery.isLoading ? "…" : formatLatency(hist?.p90_sec ?? null)}
 </p>
 </div>
 <div className={CARD}>
 <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
 p99
 </p>
 <p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900">
 {histQuery.isLoading ? "…" : formatLatency(hist?.p99_sec ?? null)}
 </p>
 </div>
 </section>

 {/* Overall histogram */}
 <section
 className={CARD}
 aria-label="Overall latency histogram"
 data-testid="conversion-timing-analysis-histogram"
 >
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Latency histogram</h2>
 <p className="mt-1 text-xs text-zinc-500">
 Bars show the number of pairs in each latency bucket.
 </p>
 {histQuery.isLoading ? (
 <p
 className="mt-4 text-sm text-zinc-500"
 data-testid="conversion-timing-analysis-loading"
 >
 Loading.
 </p>
 ) : histQuery.error ? (
 <p className="mt-4 text-sm text-red-600" role="alert">
 {histQuery.error.message}
 </p>
 ) : total === 0 ? (
 <p
 className="mt-4 text-sm text-zinc-500"
 data-testid="conversion-timing-analysis-empty"
 >
 No data.
 </p>
 ) : (
 <div className="mt-6 flex items-end gap-3 h-56">
 {buckets.map((b) => {
 const h = barHeightPct(b.count, hMax);
 return (
 <div
 key={b.id}
 className="flex flex-1 flex-col items-center gap-2"
 data-testid={`conversion-timing-analysis-bar-${b.id}`}
 >
 <p className="text-xs font-medium tabular-nums text-zinc-700">
 {b.count.toLocaleString()}
 </p>
 <div className="flex w-full flex-1 items-end">
 <div
 className={`w-full rounded-t-[4px] ${BAR_FILL}`}
 style={{ height: `${h}%` }}
 aria-label={`${b.label}: ${b.count}`}
 />
 </div>
 <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
 {b.label}
 </p>
 </div>
 );
 })}
 </div>
 )}
 </section>

 {/* Breakdown */}
 <section className={CARD} aria-label="Breakdown">
 <div className="flex items-center justify-between gap-4">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Breakdown</h2>
 <div
 className="inline-flex rounded-[6px] border border-zinc-200 bg-zinc-50 p-0.5 text-xs"
 role="tablist"
 aria-label="Breakdown dimension"
 >
 {(["campaign", "affiliate"] as const).map((d) => (
 <button
 key={d}
 type="button"
 role="tab"
 aria-selected={dimension === d}
 data-testid={`conversion-timing-analysis-dim-${d}`}
 className={`rounded-[4px] px-3 py-1 font-medium capitalize ${
 dimension === d
 ? "bg-white text-zinc-900 shadow-sm"
 : "text-zinc-500 hover:text-zinc-800"
 }`}
 onClick={() => setDimension(d)}
 >
 {d}
 </button>
 ))}
 </div>
 </div>

 <div className="mt-4 overflow-x-auto">
 {breakdownQuery.isLoading ? (
 <p className="text-sm text-zinc-500">Loading.</p>
 ) : breakdownQuery.error ? (
 <p className="text-sm text-red-600" role="alert">
 {breakdownQuery.error.message}
 </p>
 ) : breakdown.length === 0 ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="conversion-timing-analysis-breakdown-empty"
 >
 No data.
 </p>
 ) : (
 <table
 className="w-full border-collapse"
 data-testid="conversion-timing-analysis-breakdown-table"
 >
 <thead className="bg-zinc-50">
 <tr>
 <th className={TH}>{dimension}</th>
 <th className={`${TH} text-right`}>Total</th>
 <th className={`${TH} text-right`}>Median</th>
 <th className={`${TH} text-right`}>p90</th>
 <th className={`${TH} text-right`}>p99</th>
 <th className={TH}>Distribution</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-zinc-200">
 {breakdown.map((row) => (
 <tr
 key={row.key}
 data-testid={`conversion-timing-analysis-breakdown-row-${row.key}`}
 >
 <td className={`${TD} font-medium`}>{row.key}</td>
 <td className={`${TD} text-right tabular-nums`}>
 {row.total.toLocaleString()}
 </td>
 <td className={`${TD} text-right tabular-nums`}>
 {formatLatency(row.median_sec)}
 </td>
 <td className={`${TD} text-right tabular-nums`}>
 {formatLatency(row.p90_sec)}
 </td>
 <td className={`${TD} text-right tabular-nums`}>
 {formatLatency(row.p99_sec)}
 </td>
 <td className={TD}>
 <div className="flex w-40 gap-0.5">
 {row.buckets.map((b) => (
 <div
 key={b.id}
 className={`h-3 rounded-[2px] ${BAR_FILL}`}
 style={{
 flexGrow: Math.max(
 sharePct(b.count, row.total),
 b.count > 0 ? 2 : 0,
 ),
 flexBasis: 0,
 opacity: b.count > 0 ? 1 : 0.12,
 }}
 aria-label={`${b.label}: ${b.count}`}
 />
 ))}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 )}
 </div>
 </section>
 </main>
	);
}

export default ConversionTimingAnalysisPage;
